/**
 * F60 — /ops/logs: search, tail and export the production API's logs.
 * Permission `ops_logs:read` (super_admin only).
 *
 * The applied search lives in the URL (shareable, survives reload). Each apply
 * runs one Logs Insights scan; "Load older" follows the signed cursor; Live
 * polls the free FilterLogEvents tail every 10 s (paused while the tab is
 * hidden, off after 15 minutes, only for unfiltered searches). Lines arrive
 * secret-redacted from the API.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import type { OpsLogEvent, OpsLogsInfo, OpsLogsScanMeta } from '@bravemove/types';
import {
  Badge,
  Banner,
  Button,
  EmptyState,
  SegmentedControl,
  Skeleton,
  Switch,
  Table,
  cn,
  type TableColumn,
  usePollWhileVisible,
} from '@bravemove/ui-web';
import { adminOpsLogsApi, type OpsRange } from '../../api/opsLogs';
import { PageHeader } from '../../components/PageHeader';
import { OpsNav } from './OpsNav';
import { ScanMeta } from './ScanMeta';
import { ExportLogsModal } from './components/ExportLogsModal';
import { LogDetailDrawer } from './components/LogDetailDrawer';
import { LogFilterBar } from './components/LogFilterBar';
import {
  appendOlder,
  canLiveTail,
  errorKey,
  formatLogTime,
  levelTone,
  mergeNewest,
  paramsFromSearch,
  rangeForSearch,
  readTimeZoneMode,
  roundMs,
  searchFromParams,
  shortId,
  statusTone,
  writeTimeZoneMode,
  type LogsSearch,
  type TimeZoneMode,
} from './opsHelpers';

const LIVE_INTERVAL_MS = 10_000;
const LIVE_MAX_MS = 15 * 60_000;
const RATE_LIMIT_COOLDOWN_MS = 5_000;

export function OpsLogsPage() {
  const { t, locale } = useI18n();
  const [params, setParams] = useSearchParams();
  const searchKey = params.toString();
  const [search, setSearch] = useState<LogsSearch>(() => searchFromParams(params));
  const [seededKey, setSeededKey] = useState(searchKey);
  if (seededKey !== searchKey) {
    setSeededKey(searchKey);
    setSearch(searchFromParams(new URLSearchParams(searchKey)));
  }

  const [info, setInfo] = useState<OpsLogsInfo | null>(null);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [range, setRange] = useState<OpsRange>(() => rangeForSearch(search));
  const [rows, setRows] = useState<OpsLogEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [meta, setMeta] = useState<OpsLogsScanMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coolingDown, setCoolingDown] = useState(false);
  const [live, setLive] = useState(false);
  const [timeZone, setTimeZone] = useState<TimeZoneMode>(() => readTimeZoneMode());
  const [selected, setSelected] = useState<OpsLogEvent | null>(null);
  const [exporting, setExporting] = useState(false);
  /** Bumped to re-run an unchanged search (a rolling preset moves forward). */
  const [runId, setRunId] = useState(0);

  const rowsRef = useRef<OpsLogEvent[]>([]);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  // Info: provider mode, retention, route catalog.
  useEffect(() => {
    let cancelled = false;
    adminOpsLogsApi
      .getInfo()
      .then((value) => {
        if (!cancelled) setInfo(value);
      })
      .catch((err: unknown) => {
        if (!cancelled) setInfoError(errorKey(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // First page whenever the applied search changes.
  useEffect(() => {
    let cancelled = false;
    const applied = searchFromParams(new URLSearchParams(searchKey));
    const frozen = rangeForSearch(applied);
    const run = async () => {
      setLoading(true);
      setError(null);
      setRows([]);
      setNextCursor(null);
      setRange(frozen);
      try {
        const page = await adminOpsLogsApi.search({ ...frozen, ...applied.filters });
        if (cancelled) return;
        setRows([...page.data]);
        setNextCursor(page.nextCursor);
        setMeta(page.meta);
      } catch (err) {
        if (cancelled) return;
        const key = errorKey(err);
        setError(key);
        if (key === 'RATE_LIMITED') {
          setCoolingDown(true);
          setTimeout(() => setCoolingDown(false), RATE_LIMIT_COOLDOWN_MS);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [searchKey, runId]);

  // Live tail.
  const liveAllowed = canLiveTail(search);
  const liveOn = live && liveAllowed;
  const minLevel = search.filters.minLevel;
  // F61 (A6) — the shared visibility-aware poller: nothing while the tab is
  // hidden, and an immediate tick when it comes back (the old interval kept
  // ticking hidden and only skipped the request).
  const liveStartedAt = useRef(0);
  useEffect(() => {
    if (liveOn) liveStartedAt.current = Date.now();
  }, [liveOn]);
  const liveTick = useCallback(async () => {
    if (Date.now() - liveStartedAt.current > LIVE_MAX_MS) {
      setLive(false);
      return;
    }
    try {
      const fresh = await adminOpsLogsApi.getRecent({
        ...(rowsRef.current[0] ? { since: rowsRef.current[0].timestamp } : {}),
        ...(minLevel ? { minLevel } : {}),
        limit: 200,
      });
      if (fresh.length > 0) setRows((current) => mergeNewest(fresh, current));
    } catch (err) {
      setError(errorKey(err));
      setLive(false);
    }
  }, [minLevel]);
  usePollWhileVisible(liveTick, { intervalMs: LIVE_INTERVAL_MS, enabled: liveOn });

  const apply = (next: LogsSearch) => {
    setLive(false);
    const nextParams = paramsFromSearch(next);
    if (nextParams.toString() === searchKey) setRunId((n) => n + 1);
    else setParams(nextParams);
  };

  const loadOlder = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const page = await adminOpsLogsApi.search({
        ...range,
        ...search.filters,
        cursor: nextCursor,
      });
      setRows((current) => appendOlder(current, page.data));
      setNextCursor(page.nextCursor);
      setMeta(page.meta);
    } catch (err) {
      setError(errorKey(err));
    } finally {
      setLoadingMore(false);
    }
  };

  const showRequest = (requestId: string) => {
    setSelected(null);
    apply({ preset: '24h', filters: { requestId } });
  };

  const onTimeZone = (mode: TimeZoneMode) => {
    setTimeZone(mode);
    writeTimeZoneMode(mode);
  };

  const spansDays = Date.parse(range.to) - Date.parse(range.from) > 24 * 3_600_000;
  const blocked = infoError === 'OPS_LOGS_NOT_CONFIGURED' || error === 'OPS_LOGS_NOT_CONFIGURED';

  const columns: TableColumn<OpsLogEvent>[] = [
    {
      key: 'time',
      header: t('admin:opsLogs.columns.time'),
      cell: (e) => (
        <span className="whitespace-nowrap font-mono text-[12px] text-ink-2">
          {formatLogTime(e.timestamp, timeZone, locale, spansDays)}
        </span>
      ),
      width: '1%',
    },
    {
      key: 'level',
      header: t('admin:opsLogs.columns.level'),
      cell: (e) => (
        <Badge tone={levelTone(e.level)} size="sm">
          {e.level ? t(`admin:opsLogs.level.${e.level}`) : t('admin:opsLogs.level.raw')}
        </Badge>
      ),
      width: '1%',
    },
    {
      key: 'message',
      header: t('admin:opsLogs.columns.message'),
      cell: (e) => (
        <div className="min-w-0 max-w-[48ch]">
          <div className="truncate font-mono text-[12.5px] text-ink">
            {e.method ? <span className="me-1.5 text-ink-3">{e.method}</span> : null}
            {e.route ?? e.url ?? e.msg ?? e.raw}
          </div>
          {e.route || e.url ? <div className="truncate text-[12px] text-ink-3">{e.msg}</div> : null}
          {e.error ? <div className="truncate text-[12px] text-danger">{e.error}</div> : null}
        </div>
      ),
    },
    {
      key: 'status',
      header: t('admin:opsLogs.columns.status'),
      cell: (e) =>
        e.statusCode === null ? null : (
          <Badge tone={statusTone(e.statusCode)} size="sm">
            {e.statusCode}
          </Badge>
        ),
      align: 'end',
      width: '1%',
    },
    {
      key: 'ms',
      header: t('admin:opsLogs.columns.duration'),
      cell: (e) =>
        e.responseTime === null ? null : (
          <span
            className={cn(
              'whitespace-nowrap font-mono tabular-nums',
              e.responseTime > 1000 ? 'text-warning' : 'text-ink-2',
            )}
          >
            {t('admin:opsLogs.units.ms', { value: roundMs(e.responseTime) })}
          </span>
        ),
      align: 'end',
      width: '1%',
    },
    {
      key: 'request',
      header: t('admin:opsLogs.columns.request'),
      cell: (e) =>
        e.reqId ? (
          <span className="font-mono text-[12px] text-ink-3">{shortId(e.reqId)}</span>
        ) : null,
      width: '1%',
    },
    {
      key: 'actor',
      header: t('admin:opsLogs.columns.actor'),
      cell: (e) =>
        e.adminId ? (
          <span className="font-mono text-[12px] text-ink-3">
            {t('admin:opsLogs.actor.admin', { id: shortId(e.adminId) })}
          </span>
        ) : e.userId ? (
          <span className="font-mono text-[12px] text-ink-3">
            {t('admin:opsLogs.actor.user', { id: shortId(e.userId) })}
          </span>
        ) : null,
      width: '1%',
    },
  ];

  return (
    <div className="space-y-5" data-testid="ops-logs-page">
      <PageHeader
        eyebrow={t('admin:opsLogs.eyebrow')}
        title={t('admin:opsLogs.title')}
        subtitle={t('admin:opsLogs.subtitle')}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <SegmentedControl<TimeZoneMode>
              size="sm"
              value={timeZone}
              onChange={onTimeZone}
              aria-label={t('admin:opsLogs.timeZone.label')}
              options={[
                { value: 'local', label: t('admin:opsLogs.timeZone.local') },
                { value: 'utc', label: t('admin:opsLogs.timeZone.utc') },
              ]}
            />
            <Switch
              label={t('admin:opsLogs.live.label')}
              checked={liveOn}
              disabled={!liveAllowed || blocked}
              onChange={(e) => setLive(e.target.checked)}
              data-testid="ops-live-toggle"
            />
            <Button
              variant="secondary"
              size="sm"
              icon="download"
              disabled={blocked}
              onClick={() => setExporting(true)}
              data-testid="ops-export-open"
            >
              {t('admin:opsLogs.export.open')}
            </Button>
          </div>
        }
      />

      <OpsNav />

      {info?.mode === 'fake' ? (
        <div data-testid="ops-fake-banner">
          <Banner tone="info">{t('admin:opsLogs.banners.fake')}</Banner>
        </div>
      ) : null}
      {blocked ? (
        <div data-testid="ops-not-configured">
          <Banner tone="warning" title={t('admin:opsLogs.banners.notConfiguredTitle')}>
            {t('admin:opsLogs.banners.notConfigured')}
          </Banner>
        </div>
      ) : null}
      {!liveAllowed ? (
        <p className="text-[12px] text-ink-3">{t('admin:opsLogs.live.filteredHint')}</p>
      ) : null}

      <div className="rounded-xl border border-hairline bg-surface p-4">
        <LogFilterBar
          value={search}
          routes={info?.routes ?? []}
          disabled={loading || coolingDown}
          onApply={apply}
        />
      </div>

      {error && error !== 'OPS_LOGS_NOT_CONFIGURED' ? (
        <div data-testid="ops-logs-error">
          <Banner tone="danger">{t(`admin:opsLogs.errors.${error}`)}</Banner>
        </div>
      ) : null}

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 text-[12.5px] text-ink-3">
          <span data-testid="ops-logs-count">
            {t('admin:opsLogs.table.count', { count: rows.length })}
          </span>
          {meta ? <ScanMeta meta={meta} /> : null}
        </div>

        {loading ? (
          <div className="space-y-2" data-testid="ops-logs-loading">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className="h-9 w-full rounded-md" />
            ))}
          </div>
        ) : (
          <div data-testid="ops-logs-table">
            <Table
              data={rows}
              columns={columns}
              rowKey={(e) => e.id}
              density="compact"
              stickyHeader
              onRowPress={setSelected}
              ariaLabel={t('admin:opsLogs.table.aria')}
              empty={
                <EmptyState
                  title={t('admin:opsLogs.table.emptyTitle')}
                  description={t('admin:opsLogs.table.emptyBody')}
                />
              }
            />
          </div>
        )}

        {nextCursor && !loading ? (
          <div className="flex justify-center">
            <Button
              variant="secondary"
              size="sm"
              loading={loadingMore}
              onClick={() => void loadOlder()}
              data-testid="ops-load-older"
            >
              {t('admin:opsLogs.table.loadOlder')}
            </Button>
          </div>
        ) : null}
      </div>

      <LogDetailDrawer
        event={selected}
        timeZone={timeZone}
        onClose={() => setSelected(null)}
        onShowRequest={showRequest}
      />
      <ExportLogsModal
        open={exporting}
        range={range}
        filters={search.filters}
        onClose={() => setExporting(false)}
      />
    </div>
  );
}
